import { html, LitElement, svg } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { cond } from '../common/directives/cond.js'
import componentStyle from '../common/styles/Component.css'
import style from './Progress.css'

const CX = 11.25
const CY = 11.25
const OUTER_RADIUS = 9
const INNER_RADIUS = 6

let instanceCounter = 0

/**
 * Generates an SVG path for a pie sector starting from 12 o'clock, sweeping clockwise.
 * @param percent - The progress percentage (0-100)
 * @returns The SVG path data string
 */
function sectorPath(percent: number): string {
  const p = Math.max(0, Math.min(100, percent))
  if (p === 0)
    return ''
  if (p === 100) {
    return `M${CX},${CY - INNER_RADIUS}
            A${INNER_RADIUS},${INNER_RADIUS} 0 1 1 ${CX},${CY + INNER_RADIUS}
            A${INNER_RADIUS},${INNER_RADIUS} 0 1 1 ${CX},${CY - INNER_RADIUS} Z`
  }
  const sweep = (p / 100) * 360
  const theta = (-90 + sweep) * (Math.PI / 180)
  const ex = CX + INNER_RADIUS * Math.cos(theta)
  const ey = CY + INNER_RADIUS * Math.sin(theta)
  const large = sweep > 180 ? 1 : 0
  return `M${CX},${CY}
          L${CX},${CY - INNER_RADIUS}
          A${INNER_RADIUS},${INNER_RADIUS} 0 ${large},1 ${ex},${ey}
          Z`
}

/**
 * Progress component is used to display a circular pie-chart style progress indicator.
 * You can customize the size and color of the progress indicator with the
 * provided properties.
 *
 * @status ready
 * @category feedback
 * @cssprop [--n-progress-color=var(--n-color-accent)] - Controls the color of the progress indicator.
 */
@customElement('nord-progress')
export default class Progress extends LitElement {
  static styles = [componentStyle, style]

  /**
   * The size of the progress indicator.
   */
  @property({ reflect: true }) size: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' = 'm'

  /**
   * The color of the progress indicator.
   * Can accept any valid CSS color value, including custom properties.
   * Takes precedence over the `--n-progress-color` CSS custom property.
   */
  @property({ reflect: true }) color?: string

  /**
   * An accessible label for the progress indicator.
   * If no label is supplied, the component is hidden from assistive technology.
   */
  @property({ reflect: true }) label?: string

  /**
   * The progress percentage value.
   */
  @property({ reflect: true, type: Number }) progress: number = 0

  /**
   * Internal state for the animated progress value.
   */
  @state() private _animatedProgress: number = 0

  /**
   * Animation frame ID for cleanup.
   */
  private _animationId: number | null = null

  /**
   * Target progress for animation.
   */
  private _targetProgress: number = 0

  /**
   * Unique ID for this component instance, used for SVG clipPath.
   */
  private _instanceId: number = 0

  constructor() {
    super()
    this._instanceId = instanceCounter++
  }

  connectedCallback() {
    super.connectedCallback()
    this._animatedProgress = this.progress
    this._targetProgress = this.progress
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this._animationId !== null) {
      cancelAnimationFrame(this._animationId)
      this._animationId = null
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('progress')) {
      this._targetProgress = Math.max(0, Math.min(100, this.progress))
      this._animate()
    }
  }

  private _animate() {
    if (this._animationId !== null) {
      cancelAnimationFrame(this._animationId)
    }

    const animate = () => {
      const diff = this._targetProgress - this._animatedProgress
      if (Math.abs(diff) < 0.5) {
        this._animatedProgress = this._targetProgress
        this._animationId = null
        return
      }
      // Ease towards target with smooth interpolation
      this._animatedProgress += diff * 0.15
      this._animationId = requestAnimationFrame(animate)
    }

    this._animationId = requestAnimationFrame(animate)
  }

  private get _clampedProgress(): number {
    return Math.max(0, Math.min(100, this._animatedProgress))
  }

  /**
   * Check if the color is a semantic value handled by CSS.
   */
  private get _isSemanticColor(): boolean {
    return ['success', 'warning', 'danger', 'highlight', 'info'].includes(this.color ?? '')
  }

  /**
   * Get the custom color style if not a semantic value.
   */
  private get _customColorStyle(): string | undefined {
    if (this.color && !this._isSemanticColor) {
      return `--_n-progress-color:${this.color}`
    }
    return undefined
  }

  render() {
    const hasLabel = Boolean(this.label)
    const pathData = sectorPath(this._clampedProgress)
    const clipId = `n-progress-clip-${this._instanceId}`

    return html`
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 22.5 22.5"
        fill="none"
        role=${cond(hasLabel, 'progressbar')}
        aria-label=${ifDefined(this.label)}
        aria-valuenow=${cond(hasLabel, Math.round(this._clampedProgress))}
        aria-valuemin=${cond(hasLabel, '0')}
        aria-valuemax=${cond(hasLabel, '100')}
        aria-hidden=${cond(!hasLabel, 'true')}
        style=${ifDefined(this._customColorStyle)}
      >
        <g clip-path="url(#${clipId})">
          <rect x="1.25" y="1.25" width="20" height="20" rx="10" fill="var(--_n-progress-background)" />
          <circle
            cx="${CX}"
            cy="${CY}"
            r="${OUTER_RADIUS}"
            fill="var(--_n-progress-background)"
            stroke="var(--_n-progress-color)"
            stroke-width="2"
          />
          ${svg`<path fill="var(--_n-progress-color)" d="${pathData}" />`}
        </g>
        <rect
          x="0.625"
          y="0.625"
          width="21.25"
          height="21.25"
          rx="10.625"
          stroke="var(--_n-progress-background)"
          stroke-width="1.25"
        />
        <defs>
          <clipPath id="${clipId}">
            <rect x="1.25" y="1.25" width="20" height="20" rx="10" fill="white" />
          </clipPath>
        </defs>
      </svg>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-progress': Progress
  }
}
